import { differenceBy, get, set } from 'lodash';
import { isNotNillOrEmpty } from 'src/common/utils/string';
import { unWrapMaybe } from 'src/common/utils/unWrapMaybe';
import type { GQLUpdateCoverSheetFieldData } from 'src/generated/graph-api';
import { sanitizeNameOfIndexableIndicator } from 'src/views/Coversheet/utils/indexifyValues';
import type {
  Card,
  CoverSheetConfig,
} from '@/views/Coversheet/configurations/types';

const CARD_TITLE_INDEXABLE_INDICATOR = /\sx$/gm;
const CARD_NAME_INDEXABLE_INDICATOR = /.x./;

export const getIndexedTitle = (title: string, index: number) =>
  title.replace(CARD_TITLE_INDEXABLE_INDICATOR, ` ${index + 1}`);

export const removeIndexedTitle = (title: string) =>
  title.replace(CARD_TITLE_INDEXABLE_INDICATOR, '');

export const getIndexedName = (title: string, index: number) =>
  title.replace(CARD_NAME_INDEXABLE_INDICATOR, `.${index}.`);

export const getMissingFieldLabel = (
  index: number,
  isAddable: boolean,
  label: string,
  cardTitle?: string,
) => {
  return isAddable && cardTitle
    ? `${getIndexedTitle(cardTitle, index)} ${label}`
    : label;
};

export const getCardFormDataPath = (
  card:
    | { entity?: string | null; entityType?: string | null }
    | undefined
    | null,
  order?: number,
) =>
  [
    unWrapMaybe<string>(card?.entity) ?? '',
    unWrapMaybe<string>(card?.entityType) ?? '',
    order ?? '',
  ]
    .filter((path) => `${path}`.length)
    .join('.');

export const getEmptyCardValues = (card: { fields: { name: string }[] }) =>
  card.fields.reduce(
    (agg, field) => set(agg, sanitizeNameOfIndexableIndicator(field.name), ''),
    {},
  );

// biome-ignore lint/suspicious/noExplicitAny: Preserve existing implementation
export const cardHasValues = (card: Card, data: any) =>
  card.fields.some(
    (field) =>
      field.type !== 'hidden' &&
      isNotNillOrEmpty(
        get(data, sanitizeNameOfIndexableIndicator(field.name), ''),
      ),
  );

export const getCardEntityFields = (
  card: Card,
  defaultValues?: GQLUpdateCoverSheetFieldData[],
) => {
  const sanitizedCardFields = card.fields
    .filter((f) => !!f.entity)
    .map((f) => ({
      name: sanitizeNameOfIndexableIndicator(f.name),
      value: '',
    }));
  const uniqueDefaultValueFields = differenceBy(
    defaultValues ?? [],
    sanitizedCardFields,
    'name',
  );

  return sanitizedCardFields.reduce((agg, field) => {
    const defaultValue = defaultValues?.find((f) => f.name === field.name);

    if (defaultValue) {
      agg.push(defaultValue);
    } else {
      agg.push(field);
    }

    return agg;
  }, uniqueDefaultValueFields);
};

export const getCardId = (cardId: string, index: number) =>
  `${cardId}-${index}`;

export const findCardConfigByEntityAndType = (
  entity: string,
  entityType: string,
  config?: CoverSheetConfig,
) => {
  let card: Card | undefined;
  for (const section of config?.sections ?? []) {
    card = section.cards.find(
      (c) => c.entity === entity && c.entityType === entityType,
    );
    if (card) {
      break;
    }
  }
  return card;
};
